(function () {
  'use strict';

  const CONFIG = {
    paddleProductId: 15400353399021,
    feeVariantId: 67646001643757
  };

  let isReconciling = false;


  /*
   * ---------------------------------------------------------
   * CART DATA
   * ---------------------------------------------------------
   */

  async function getCart() {
    const response = await fetch(
      window.Shopify.routes.root + 'cart.js',
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Could not retrieve cart.');
    }

    return response.json();
  }


  /*
   * ---------------------------------------------------------
   * DAWN CART SECTIONS
   * ---------------------------------------------------------
   *
   * These functions deliberately follow the way Dawn itself
   * refreshes the cart.
   */


  /*
   * Refresh the cart drawer.
   *
   * Dawn's own CartItems.onCartUpdate() does this by requesting
   * the cart-drawer section and replacing:
   *
   *   cart-drawer-items
   *   .cart-drawer__footer
   */
  async function refreshCartDrawer() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');

    if (!cartDrawerItems) {
      return;
    }

    const response = await fetch(
      window.Shopify.routes.root + 'cart?section_id=cart-drawer'
    );

    if (!response.ok) {
      throw new Error('Could not refresh cart drawer.');
    }

    const responseText = await response.text();

    const html = new DOMParser().parseFromString(
      responseText,
      'text/html'
    );

    const selectors = [
      'cart-drawer-items',
      '.cart-drawer__footer'
    ];

    selectors.forEach(function (selector) {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);

      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    });

    /*
     * Make sure the drawer's empty/non-empty state is correct.
     */
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      const cart = await getCart();

      cartDrawer.classList.toggle(
        'is-empty',
        cart.item_count === 0
      );
    }
  }


  /*
   * Refresh the actual /cart page.
   *
   * This follows the section structure from your version
   * of Dawn.
   */
  async function refreshCartPage() {
    if (!window.location.pathname.endsWith('/cart')) {
      return;
    }

    const mainCartItems = document.getElementById(
      'main-cart-items'
    );

    const mainCartFooter = document.getElementById(
      'main-cart-footer'
    );

    const sections = [];

    if (mainCartItems) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents'
      });
    }

    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents'
      });
    }

    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section'
    });

    sections.push({
      id: 'cart-live-region-text',
      section: 'cart-live-region-text',
      selector: '.shopify-section'
    });


    /*
     * Request the current rendered sections.
     */
    const sectionIds = sections.map(function (section) {
      return section.section;
    });

    const response = await fetch(
      window.Shopify.routes.root +
      '?sections=' +
      sectionIds.join(',')
    );

    if (!response.ok) {
      throw new Error('Could not refresh cart page.');
    }

    const renderedSections = await response.json();


    sections.forEach(function (section) {
      const html =
        renderedSections[section.section];

      if (!html) {
        return;
      }

      const target = document.getElementById(
        section.id
      );

      if (!target) {
        return;
      }

      const parsed = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const source = parsed.querySelector(
        section.selector
      );

      const destination = target.querySelector(
        section.selector
      );

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
      }
    });
  }


  /*
   * Refresh both cart interfaces.
   */
  async function refreshCartUI() {
    await Promise.all([
      refreshCartDrawer(),
      refreshCartPage()
    ]);
  }


  /*
   * ---------------------------------------------------------
   * FEE OPERATIONS
   * ---------------------------------------------------------
   */

  async function addFee() {
    const response = await fetch(
      window.Shopify.routes.root + 'cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: CONFIG.feeVariantId,
              quantity: 1
            }
          ]
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        'Could not add setup fee: ' + errorText
      );
    }

    return response.json();
  }


  async function removeFee(feeItem) {
    const response = await fetch(
      window.Shopify.routes.root + 'cart/change.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: feeItem.key,
          quantity: 0
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        'Could not remove setup fee: ' + errorText
      );
    }

    return response.json();
  }


  async function setFeeQuantity(feeItem) {
    const response = await fetch(
      window.Shopify.routes.root + 'cart/change.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: feeItem.key,
          quantity: 1
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(
        'Could not set setup fee quantity: ' + errorText
      );
    }

    return response.json();
  }


  /*
   * ---------------------------------------------------------
   * RECONCILIATION
   * ---------------------------------------------------------
   */

  async function reconcileFee() {
    /*
     * Never allow two reconciliation operations to run
     * simultaneously.
     */
    if (isReconciling) {
      return;
    }

    isReconciling = true;

    try {
      const cart = await getCart();

      /*
       * Does the cart contain Customizable Paddle?
       */
      const paddleInCart = cart.items.some(function (item) {
        return Number(item.product_id) ===
          CONFIG.paddleProductId;
      });


      /*
       * Find ALL setup-fee lines.
       */
      const feeItems = cart.items.filter(function (item) {
        return Number(item.variant_id) ===
          CONFIG.feeVariantId;
      });


      let cartChanged = false;


      if (paddleInCart) {

        /*
         * Paddle exists.
         *
         * We want exactly ONE fee at quantity 1.
         */

        if (feeItems.length === 0) {

          await addFee();

          cartChanged = true;

        } else {

          /*
           * Remove duplicate fee lines.
           */
          for (let i = 1; i < feeItems.length; i++) {
            await removeFee(feeItems[i]);

            cartChanged = true;
          }


          /*
           * Force the remaining fee to quantity 1.
           */
          if (feeItems[0].quantity !== 1) {

            await setFeeQuantity(feeItems[0]);

            cartChanged = true;
          }
        }

      } else {

        /*
         * No Customizable Paddle.
         *
         * Therefore there must be no setup fee.
         */

        for (const feeItem of feeItems) {

          await removeFee(feeItem);

          cartChanged = true;
        }
      }


      /*
       * Only refresh the UI if WE changed the cart.
       */
      if (cartChanged) {
        await refreshCartUI();
      }

    } catch (error) {

      console.error(
        '[Custom Paddle Fee]',
        error
      );

    } finally {

      isReconciling = false;
    }
  }


  /*
   * ---------------------------------------------------------
   * INITIAL LOAD
   * ---------------------------------------------------------
   */

  reconcileFee();


  /*
   * ---------------------------------------------------------
   * DAWN CART UPDATE SUBSCRIPTION
   * ---------------------------------------------------------
   *
   * This is the important change.
   *
   * Current Dawn publishes cart changes through:
   *
   *     PUB_SUB_EVENTS.cartUpdate
   *
   * rather than relying on the DOM events we were using
   * previously.
   */

  /*
   * ---------------------------------------------------------
   * DAWN CART UPDATE SUBSCRIPTION
   * ---------------------------------------------------------
   */

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {

    subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      function () {
        reconcileFee();
      }
    );

  } else {

    console.warn(
      '[Custom Paddle Fee] Dawn cart subscription API was not found.'
    );
  }


  /*
   * ---------------------------------------------------------
   * EXTRA CART-PAGE / DRAWER SAFETY NET
   * ---------------------------------------------------------
   *
   * Dawn can sometimes finish a cart operation without giving
   * our fee logic a usable notification.
   *
   * When the customer explicitly removes/changes something,
   * check the cart again shortly afterward.
   *
   * We deliberately do NOT constantly poll the cart.
   */


  function scheduleReconciliation() {

    /*
     * Give Dawn a moment to finish its own cart request.
     */
    setTimeout(function () {
      reconcileFee();
    }, 300);

    /*
     * Second check for slower cart operations.
     */
    setTimeout(function () {
      reconcileFee();
    }, 800);

    /*
     * Final check for particularly slow connections.
     */
    setTimeout(function () {
      reconcileFee();
    }, 1500);
  }


  /*
   * Catch clicks on Dawn's remove buttons.
   *
   * This covers both the cart page and cart drawer.
   */
  document.addEventListener(
    'click',
    function (event) {

      const removeButton =
        event.target.closest('cart-remove-button');

      if (!removeButton) {
        return;
      }

      scheduleReconciliation();
    },
    true
  );


  /*
   * Catch cart-page form submissions.
   *
   * This covers quantity changes made from the main cart page.
   */
  document.addEventListener(
    'submit',
    function (event) {

      const form = event.target;

      if (!form) {
        return;
      }

      if (
        form.matches('cart-items form') ||
        form.closest('cart-items')
      ) {
        scheduleReconciliation();
      }

    },
    true
  );


  /*
   * Also catch quantity input changes directly.
   */
  document.addEventListener(
    'change',
    function (event) {

      const input = event.target;

      if (
        input &&
        input.matches(
          'cart-items input[name="updates[]"], ' +
          'cart-drawer-items input[name="updates[]"]'
        )
      ) {
        scheduleReconciliation();
      }

    },
    true
  );

})();